<!--
  Topbar — the persistent operator context strip above every page.

  Left:  breadcrumb (section / page) derived from the current route.
  Right: the live counters (events/min, 24 h volume, sources up, open
         detections), the enforcement flag, a manual refresh, and the
         operator avatar.

  It owns the shell's 15 s poll: `refreshShellStats()` fills the shared store
  that the sidebar badges also read, so the chrome makes one round-trip, not
  two. The bar is read-only — each counter links to the page you'd drill into.
-->
<script lang="ts">
	import { Badge, IconButton, Topbar, avatarVariants, type Tone } from '@navitech/ui';
	import Clock from '@lucide/svelte/icons/clock';
	import RefreshCw from '@lucide/svelte/icons/refresh-cw';
	import Shield from '@lucide/svelte/icons/shield';
	import { createQuery } from '../lib/query.svelte';
	import { fmtCount, fmtRelative } from '../lib/format';
	import { link, route } from '../lib/router';
	import { shellStats, refreshShellStats } from '../lib/shellStats.svelte';
	import { features } from '../lib/features.svelte';
	import { identity } from '../lib/identity.svelte';

	// One poll for the whole shell. `createQuery` gives us the initial fetch +
	// a `status` we can show on the refresh button; the interval keeps it warm.
	const statsQuery = createQuery(() => refreshShellStats());

	// Polling stops while the tab is hidden and resumes with an immediate
	// refresh when it comes back. A dashboard left open in a background tab
	// used to keep hitting the API every 15 s for numbers nobody was reading —
	// per tab, per pod, forever.
	let timer: ReturnType<typeof setInterval> | null = null;
	function stopPolling() {
		if (timer) {
			clearInterval(timer);
			timer = null;
		}
	}
	function startPolling() {
		if (timer) return;
		timer = setInterval(() => statsQuery.refetch(), 15_000);
	}
	function onVisibility() {
		if (document.hidden) {
			stopPolling();
			return;
		}
		void statsQuery.refetch();
		startPolling();
	}
	$effect(() => {
		if (!document.hidden) startPolling();
		document.addEventListener('visibilitychange', onVisibility);
		return () => {
			document.removeEventListener('visibilitychange', onVisibility);
			stopPolling();
		};
	});

	// Atomic derives. Never default the whole object — see ui.md and the
	// svelte5-derived-async-quirk memory.
	const stats = $derived(shellStats.stats);
	const eventsLastHour = $derived(stats?.events_last_hour ?? 0);
	const eventsLast24h = $derived(stats?.events_last_24h ?? 0);
	const open = $derived(stats?.detections_open ?? 0);
	const sourcesUp = $derived(stats?.sources_up ?? 0);
	const sourcesTotal = $derived(stats?.sources_total ?? 0);
	const built = $derived(stats?.aggregator_built_at ?? null);

	const eventsPerMin = $derived(Math.round(eventsLastHour / 60));

	// Colour the counters by what they mean, not by decoration. Each badge also
	// says what it counts, so the colour is never the only signal.
	const openTone: Tone = $derived(open === 0 ? 'ok' : open > 5 ? 'err' : 'warn');
	const sourcesTone: Tone = $derived(
		sourcesTotal > 0 && sourcesUp === sourcesTotal ? 'ok' : sourcesUp === 0 ? 'err' : 'warn',
	);

	// Breadcrumb: section / page, matching the sidebar grouping.
	const CRUMBS: Record<string, [string, string]> = {
		dashboard: ['Operations', 'Dashboard'],
		traffic: ['Operations', 'Traffic'],
		events: ['Investigate', 'Events'],
		eventDetail: ['Investigate', 'Event detail'],
		httpRequests: ['Investigate', 'HTTP Requests'],
		domains: ['Investigate', 'Domains'],
		domainDetail: ['Investigate', 'Domain detail'],
		detections: ['Investigate', 'Detections'],
		detectionDetail: ['Investigate', 'Detection detail'],
		banReview: ['Enforcement', 'Ban Review'],
		bans: ['Enforcement', 'Bans'],
		services: ['Inventory', 'Services'],
		serviceInsights: ['Inventory', 'Service insights'],
		sources: ['Inventory', 'Sources'],
		trustedIps: ['Inventory', 'Trusted IPs'],
		clientDetail: ['Investigate', 'Client'],
		originDetail: ['Investigate', 'Origin'],
		portDetail: ['Investigate', 'Port'],
		settings: ['System', 'Settings'],
	};
	const crumb = $derived(CRUMBS[$route.name] ?? ['Operations', 'Dashboard']);

	// Avatar initials from the operator's own IP is meaningless; the session
	// has no display name yet, so the avatar tile carries a shield glyph and
	// the observed IP goes in the tooltip. The package's Avatar only draws
	// initials, so this reuses its anatomy (`avatarVariants`) around the glyph.
	const whoami = $derived(identity.ip ? `Signed in · your IP ${identity.ip}` : 'Signed in');
	const avatar = avatarVariants({ size: 'md' });

	// The UTC clock ticks once a second, but only while someone can see it.
	let nowText = $state(new Date().toUTCString().slice(17, 25));
	$effect(() => {
		let t: ReturnType<typeof setInterval> | null = null;
		const tick = () => (nowText = new Date().toUTCString().slice(17, 25));
		const sync = () => {
			if (t) clearInterval(t);
			t = document.hidden ? null : setInterval(tick, 1000);
			tick();
		};
		sync();
		document.addEventListener('visibilitychange', sync);
		return () => {
			if (t) clearInterval(t);
			document.removeEventListener('visibilitychange', sync);
		};
	});
</script>

<Topbar>
	<div class="hidden shrink-0 truncate text-[13px] whitespace-nowrap text-text-dim sm:block">
		{crumb[0]} <span class="opacity-40">/</span> <b class="font-semibold text-text">{crumb[1]}</b>
	</div>

	<div class="ml-auto flex min-w-0 items-center gap-2">
		<!-- Live counters. -->
		<a use:link href="/traffic" title="Events per minute (last hour ÷ 60)">
			<Badge pulse><span class="font-mono">{fmtCount(eventsPerMin)}</span> ev/min</Badge>
		</a>
		<a use:link href="/traffic" class="hidden xl:inline-flex" title="Events stored in the last 24 h">
			<Badge><span class="font-mono">{fmtCount(eventsLast24h)}</span> 24h</Badge>
		</a>
		<a use:link href="/sources" title="Sources reporting within the freshness window">
			<Badge tone={sourcesTone}>
				<span class="font-mono">{sourcesUp}/{sourcesTotal}</span>
				<span class="opacity-70">sources</span>
			</Badge>
		</a>
		<a use:link href="/detections" title="Open detections">
			<Badge tone={openTone}>
				<span class="font-mono">{fmtCount(open)}</span>
				<span class="opacity-70">open</span>
			</Badge>
		</a>

		{#if built}
			<Badge icon={Clock} class="hidden xl:inline-flex" title="Last hourly aggregate built">
				agg {fmtRelative(built)}
			</Badge>
		{/if}
		<Badge class="hidden font-mono 2xl:inline-flex">{nowText} UTC</Badge>

		<!-- Enforcement flag. Phase 1 records decisions only; say so. -->
		<Badge
			dot
			tone={features.enforcementEnabled ? 'warn' : 'muted'}
			class="hidden lg:inline-flex"
			title={features.enforcementEnabled
				? 'REMEDIATION_ENABLED is on — approved bans publish to the ban agent'
				: 'Observation only — bans are recorded, no firewall is touched'}
		>
			{features.enforcementEnabled ? 'Enforcing' : 'Observation only'}
		</Badge>

		<IconButton
			label="Refresh"
			title={shellStats.failed ? 'Refresh now — last refresh failed, numbers may be stale' : 'Refresh now'}
			dot={shellStats.failed}
			dotLabel="last refresh failed, numbers may be stale"
			onclick={() => statsQuery.refetch()}
		>
			<RefreshCw class={statsQuery.status === 'loading' ? 'animate-ds-spin' : undefined} aria-hidden="true" />
		</IconButton>

		<span class={avatar.root()} title={whoami}>
			<span class={avatar.tile()}><Shield class="size-4" aria-hidden="true" /></span>
		</span>
	</div>
</Topbar>
